import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { exportToCSV } from "./exportUtils";

export type ReportType =
  | "security_summary"
  | "compliance_report"
  | "failed_logins"
  | "backup_health"
  | "executive_summary";

export interface ReportConfig {
  type: ReportType;
  title: string;
  description: string;
  icon: string;
  schedule?: "weekly" | "monthly" | "daily";
}

export const REPORT_TEMPLATES: ReportConfig[] = [
  {
    type: "security_summary",
    title: "Weekly Security Summary",
    description: "Overview of security posture, alerts, failed logins, and critical events from the past 7 days.",
    icon: "Shield",
    schedule: "weekly",
  },
  {
    type: "compliance_report",
    title: "Monthly Compliance Report",
    description: "Compliance check status across all frameworks, evidence coverage, and outstanding remediation tasks.",
    icon: "CheckSquare",
    schedule: "monthly",
  },
  {
    type: "failed_logins",
    title: "Failed Login Report",
    description: "Detailed breakdown of failed authentication attempts per server with trend analysis.",
    icon: "AlertTriangle",
    schedule: "weekly",
  },
  {
    type: "backup_health",
    title: "Backup Health Report",
    description: "Backup job statuses, restore test results, and storage utilization summary.",
    icon: "Database",
    schedule: "weekly",
  },
  {
    type: "executive_summary",
    title: "Executive Summary",
    description: "High-level PDF report with security score, uptime, compliance status, and key metrics for leadership.",
    icon: "FileText",
    schedule: "monthly",
  },
];

function addHeader(doc: jsPDF, title: string, subtitle: string) {
  doc.setFontSize(20);
  doc.setTextColor(30, 30, 30);
  doc.text("ServSpect", 14, 20);
  doc.setFontSize(14);
  doc.text(title, 14, 30);
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(subtitle, 14, 37);
  doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 43);
  doc.setDrawColor(200, 200, 200);
  doc.line(14, 46, 196, 46);
  return 52;
}

export function generateSecuritySummaryPDF(data: {
  servers: any[];
  alerts: any[];
  auditLogs: any[];
}) {
  const doc = new jsPDF();
  let y = addHeader(doc, "Weekly Security Summary", "Security posture overview — past 7 days");

  // Server overview
  doc.setFontSize(12);
  doc.setTextColor(30, 30, 30);
  doc.text("Server Overview", 14, y);
  y += 6;

  const totalServers = data.servers.length;
  const onlineServers = data.servers.filter(s => s.online || s.status === "online").length;
  const criticalServers = data.servers.filter(s => s.status === "critical").length;

  autoTable(doc, {
    startY: y,
    head: [["Metric", "Value"]],
    body: [
      ["Total Servers", String(totalServers)],
      ["Online", String(onlineServers)],
      ["Critical", String(criticalServers)],
      ["Offline", String(totalServers - onlineServers)],
    ],
    theme: "striped",
    headStyles: { fillColor: [59, 130, 246] },
  });

  y = (doc as any).lastAutoTable.finalY + 10;

  // Audit events summary
  doc.text("Security Events", 14, y);
  y += 6;

  const highRisk = data.auditLogs.filter(l => l.risk_level === "high" || l.risk_level === "critical").length;
  const failedActions = data.auditLogs.filter(l => !l.success).length;

  autoTable(doc, {
    startY: y,
    head: [["Metric", "Value"]],
    body: [
      ["Total Audit Events", String(data.auditLogs.length)],
      ["High/Critical Events", String(highRisk)],
      ["Failed Actions", String(failedActions)],
    ],
    theme: "striped",
    headStyles: { fillColor: [59, 130, 246] },
  });

  y = (doc as any).lastAutoTable.finalY + 10;

  // Top alerts
  if (data.alerts.length > 0) {
    doc.text("Recent Alerts", 14, y);
    y += 6;
    const alertRows = data.alerts.slice(0, 10).map(a => [
      a.type || a.alert_type || "N/A",
      a.severity || "N/A",
      a.hostname || "N/A",
      new Date(a.created_at || a.timestamp).toLocaleDateString(),
    ]);
    autoTable(doc, {
      startY: y,
      head: [["Type", "Severity", "Server", "Date"]],
      body: alertRows,
      theme: "striped",
      headStyles: { fillColor: [59, 130, 246] },
    });
  }

  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`ServSpect — Confidential | Page ${i} of ${pageCount}`, 14, 287);
  }

  doc.save(`security-summary-${new Date().toISOString().slice(0, 10)}.pdf`);
}

export function generateComplianceReportPDF(data: {
  checks: any[];
  frameworks: any[];
  evidence: any[];
}) {
  const doc = new jsPDF();
  let y = addHeader(doc, "Monthly Compliance Report", "Framework compliance status and evidence coverage");

  // Compliance checks
  doc.setFontSize(12);
  doc.setTextColor(30, 30, 30);
  doc.text("Compliance Checks", 14, y);
  y += 6;

  const passed = data.checks.filter(c => c.status === "passed" || c.status === "compliant").length;
  const failed = data.checks.filter(c => c.status === "failed" || c.status === "non_compliant").length;

  autoTable(doc, {
    startY: y,
    head: [["Status", "Count"]],
    body: [
      ["Total Checks", String(data.checks.length)],
      ["Passed", String(passed)],
      ["Failed", String(failed)],
      ["Pending", String(data.checks.length - passed - failed)],
    ],
    theme: "striped",
    headStyles: { fillColor: [34, 197, 94] },
  });

  y = (doc as any).lastAutoTable.finalY + 10;

  // Evidence summary
  doc.text("Evidence Coverage", 14, y);
  y += 6;

  const approved = data.evidence.filter(e => e.status === "approved").length;
  const stale = data.evidence.filter(e => e.status === "stale").length;

  autoTable(doc, {
    startY: y,
    head: [["Metric", "Value"]],
    body: [
      ["Total Evidence", String(data.evidence.length)],
      ["Approved", String(approved)],
      ["Proposed", String(data.evidence.length - approved - stale)],
      ["Stale / Expired", String(stale)],
    ],
    theme: "striped",
    headStyles: { fillColor: [34, 197, 94] },
  });

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`ServSpect — Confidential | Page ${i} of ${pageCount}`, 14, 287);
  }

  doc.save(`compliance-report-${new Date().toISOString().slice(0, 10)}.pdf`);
}

export function generateFailedLoginsPDF(servers: any[]) {
  const doc = new jsPDF();
  let y = addHeader(doc, "Failed Login Report", "Authentication failure analysis across all servers");

  const rows = servers
    .filter(s => (s.failed_login_last_24h || 0) > 0)
    .sort((a, b) => (b.failed_login_last_24h || 0) - (a.failed_login_last_24h || 0))
    .map(s => [
      s.hostname,
      String(s.failed_login_last_24h || 0),
      String(s.admin_accounts_count || "N/A"),
      s.firewall_enabled ? "Yes" : "No",
      s.status || "unknown",
    ]);

  autoTable(doc, {
    startY: y,
    head: [["Server", "Failed Logins (24h)", "Admin Accounts", "Firewall", "Status"]],
    body: rows.length > 0 ? rows : [["No failed login data available", "", "", "", ""]],
    theme: "striped",
    headStyles: { fillColor: [239, 68, 68] },
  });

  doc.save(`failed-logins-${new Date().toISOString().slice(0, 10)}.pdf`);
}

export function generateBackupHealthPDF(backups: any[]) {
  const doc = new jsPDF();
  let y = addHeader(doc, "Backup Health Report", "Backup job status and restore test results");

  const rows = backups.map(b => [
    b.backup_type,
    b.status,
    b.restore_tested ? "Yes" : "No",
    b.test_result || "N/A",
    b.backup_size_mb ? `${b.backup_size_mb} MB` : "N/A",
    new Date(b.created_at).toLocaleDateString(),
  ]);

  autoTable(doc, {
    startY: y,
    head: [["Type", "Status", "Restore Tested", "Test Result", "Size", "Date"]],
    body: rows.length > 0 ? rows : [["No backup data available", "", "", "", "", ""]],
    theme: "striped",
    headStyles: { fillColor: [168, 85, 247] },
  });

  doc.save(`backup-health-${new Date().toISOString().slice(0, 10)}.pdf`);
}

export function generateExecutivePDF(data: {
  servers: any[];
  complianceChecks: any[];
  evidence: any[];
  auditLogs: any[];
}) {
  const doc = new jsPDF();
  let y = addHeader(doc, "Executive Summary", "High-level platform health and compliance overview");

  // KPIs
  doc.setFontSize(12);
  doc.text("Key Performance Indicators", 14, y);
  y += 6;

  const totalServers = data.servers.length;
  const online = data.servers.filter(s => s.status === "online" || s.online).length;
  const uptimePct = totalServers > 0 ? ((online / totalServers) * 100).toFixed(1) : "N/A";
  const compliancePassed = data.complianceChecks.filter(c => c.status === "passed" || c.status === "compliant").length;
  const compliancePct = data.complianceChecks.length > 0
    ? ((compliancePassed / data.complianceChecks.length) * 100).toFixed(1)
    : "N/A";

  autoTable(doc, {
    startY: y,
    head: [["KPI", "Value"]],
    body: [
      ["Total Monitored Servers", String(totalServers)],
      ["Infrastructure Availability", `${uptimePct}%`],
      ["Compliance Pass Rate", `${compliancePct}%`],
      ["Evidence Items", String(data.evidence.length)],
      ["Audit Events (period)", String(data.auditLogs.length)],
      ["High-Risk Events", String(data.auditLogs.filter(l => l.risk_level === "critical" || l.risk_level === "high").length)],
    ],
    theme: "grid",
    headStyles: { fillColor: [59, 130, 246] },
  });

  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text(`ServSpect — Confidential — Executive Only | Page ${i} of ${pageCount}`, 14, 287);
  }

  doc.save(`executive-summary-${new Date().toISOString().slice(0, 10)}.pdf`);
}

export function exportReportCSV(type: ReportType, data: any[]) {
  exportToCSV(data, `${type}-${new Date().toISOString().slice(0, 10)}`);
}
